import { useState } from "react";
import { Button, Form, Icon, Label, Modal, Table } from "semantic-ui-react";
import { fmtDateTime } from "../adminHelpers";
import { formatRupees, isMoney } from "../../../utils/money";

export const KIND_LABELS = { ADVANCE: "Advance", SETTLEMENT: "Settlement", REFUND: "Refund" };
export const MODE_OPTIONS = [
  { key: "CASH", value: "CASH", text: "Cash" },
  { key: "UPI", value: "UPI", text: "UPI" },
  { key: "CARD", value: "CARD", text: "Card" },
  { key: "BANK", value: "BANK", text: "Bank transfer" },
  { key: "DEPARTMENT", value: "DEPARTMENT", text: "Department transfer" },
  { key: "OTHER", value: "OTHER", text: "Other" },
];

/* Which kinds of payment make sense right now — the same rules the server
   enforces, so the form offers only what it would accept:
   before the bill is final, money in is an advance; after, a settlement up
   to what is owed; a refund only when more was paid than the final bill
   (or the stay was cancelled with no bill). */
export function allowedKinds(summary, { hasBill, billFinal }) {
  const kinds = [];
  if (!billFinal) kinds.push("ADVANCE");
  if (billFinal && Number(summary?.balanceDue) > 0) kinds.push("SETTLEMENT");
  if ((billFinal || !hasBill) && Number(summary?.refundDue) > 0) kinds.push("REFUND");
  return kinds;
}

export function suggestedAmount(kind, summary) {
  if (kind === "SETTLEMENT") return String(Number(summary?.balanceDue || 0));
  if (kind === "REFUND") return String(Number(summary?.refundDue || 0));
  return "";
}

export default function BillPayments({ payments, summary, hasBill, billFinal, canRecord, busy, onRecord, onVoid }) {
  const [voiding, setVoiding] = useState(null);
  const [reason, setReason] = useState("");
  const kinds = allowedKinds(summary, { hasBill, billFinal });

  return (
    <div>
      {payments.length === 0 ? (
        <p style={{ opacity: 0.7 }}>No payments recorded.</p>
      ) : (
        <Table compact unstackable size="small">
          <Table.Body>
            {payments.map((p) => (
              <Table.Row key={p.id} disabled={p.isVoid}>
                <Table.Cell>
                  <strong>{KIND_LABELS[p.kind]}</strong> · {MODE_OPTIONS.find((m) => m.value === p.mode)?.text || p.mode}
                  {p.isVoid && <Label size="mini" basic color="red" style={{ marginLeft: 6 }} title={p.voidReason}>Void</Label>}
                  <div style={{ fontSize: "0.85em", opacity: 0.7 }}>
                    {fmtDateTime(p.receivedAt)}
                    {[p.receiptNumber && `Slip ${p.receiptNumber}`, p.reference].filter(Boolean).map((t) => ` · ${t}`).join("")}
                    {p.receivedBy && ` · ${p.receivedBy}`}
                  </div>
                </Table.Cell>
                <Table.Cell textAlign="right" style={{ whiteSpace: "nowrap", textDecoration: p.isVoid ? "line-through" : undefined }}>
                  <strong>{p.kind === "REFUND" ? "− " : ""}{formatRupees(p.amount)}</strong>
                </Table.Cell>
                {canRecord && (
                  <Table.Cell collapsing>
                    {!p.isVoid && (
                      <Button size="mini" basic disabled={busy} onClick={() => { setReason(""); setVoiding(p); }}>
                        Void
                      </Button>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {canRecord && kinds.length > 0 && (
        <RecordPayment key={kinds.join()} kinds={kinds} summary={summary} busy={busy} onRecord={onRecord} />
      )}
      {canRecord && kinds.length === 0 && billFinal && summary?.settled && (
        <p style={{ color: "#21ba45" }}><Icon name="check circle" />Fully settled.</p>
      )}

      {voiding && (
        <Modal open size="tiny" onClose={() => setVoiding(null)}>
          <Modal.Header>Void this {KIND_LABELS[voiding.kind].toLowerCase()} of {formatRupees(voiding.amount)}?</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.TextArea id="void-reason" label="Why? (kept in the record)" rows={2} value={reason}
                onChange={(_, d) => setReason(d.value)} />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setVoiding(null)}>Keep it</Button>
            <Button negative disabled={!reason.trim() || busy}
              onClick={async () => { if (await onVoid(voiding.id, reason.trim())) setVoiding(null); }}>
              Void payment
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </div>
  );
}

function RecordPayment({ kinds, summary, busy, onRecord }) {
  const [form, setForm] = useState(() => ({
    kind: kinds[0], mode: "CASH", amount: suggestedAmount(kinds[0], summary),
    receiptNumber: "", reference: "",
  }));
  const set = (key) => (_, d) => setForm((f) => ({
    ...f, [key]: d.value, ...(key === "kind" ? { amount: suggestedAmount(d.value, summary) } : {}),
  }));
  const valid = isMoney(form.amount) && Number(form.amount) > 0;

  const submit = async () => {
    const ok = await onRecord({
      kind: form.kind, mode: form.mode, amount: form.amount.trim(),
      receipt_number: form.receiptNumber.trim(), reference: form.reference.trim(),
    });
    if (ok) setForm((f) => ({ ...f, amount: "", receiptNumber: "", reference: "" }));
  };

  return (
    <Form onSubmit={submit} size="small" style={{ marginTop: 8 }}>
      <Form.Group widths="equal">
        <Form.Select id="pay-kind" label="Type" value={form.kind} onChange={set("kind")}
          options={kinds.map((k) => ({ key: k, value: k, text: KIND_LABELS[k] }))} />
        <Form.Select id="pay-mode" label="Paid by" value={form.mode} onChange={set("mode")} options={MODE_OPTIONS} />
      </Form.Group>
      <Form.Input id="pay-amount" label="Amount (₹)" inputMode="decimal" value={form.amount}
        error={form.amount !== "" && !valid} onChange={set("amount")} />
      <Form.Group widths="equal">
        <Form.Input id="pay-receipt" label="Receipt slip no. (optional)" value={form.receiptNumber}
          onChange={set("receiptNumber")} />
        <Form.Input id="pay-ref" label="UTR / reference (optional)" value={form.reference}
          onChange={set("reference")} />
      </Form.Group>
      <Button type="submit" primary size="small" disabled={!valid || busy}>
        <Icon name="rupee sign" />Record {KIND_LABELS[form.kind].toLowerCase()}
      </Button>
    </Form>
  );
}
